/**
 * Support access (ADR-0088): one switch, off by default. On lasts seven days and
 * then turns itself off; the customer can turn it off sooner.
 *
 * The main process owns the truth, so the switch shows what main last answered
 * and never what was clicked. There is no change event: the state is read when
 * this opens, after every change, when the window is returned to, and once just
 * after the seven days are due to end.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { LoaderCircle } from 'lucide-react';

import type { SupportAccessState } from '../shared/desktop-api';

import './support-access.css';

const desktop = () => window.capturePointDesktop;

const readFailureText = 'Paige could not read support access on this computer.';
const changeFailureText = 'Support access could not be changed. Try again, or restart Paige.';
/** Main answers by its own clock, so the read is made just after the expiry rather than on it. */
const expiryReadDelayMilliseconds = 2_000;
/** The longest wait a browser timer keeps; seven days is well inside it. */
const longestTimerMilliseconds = 2_147_000_000;

const isState = (value: unknown): value is SupportAccessState =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as SupportAccessState).on === 'boolean';

const sentence = (reason: unknown, fallback: string) =>
  reason instanceof Error && reason.message.trim() ? reason.message.trim() : fallback;

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function SupportAccessCard() {
  const [state, setState] = useState<SupportAccessState | null>(null);
  const [readFailure, setReadFailure] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  // Every answer from main is numbered, so a slow read can never replace the
  // answer to a change that was asked for after it.
  const revision = useRef(0);
  const mounted = useRef(true);
  const busy = useRef(false);
  const descriptionId = useId();

  const read = useCallback((quiet: boolean) => {
    const api = desktop();
    if (!api?.getSupportAccess) {
      if (!quiet) setReadFailure(readFailureText);
      return;
    }
    const request = ++revision.current;
    if (!quiet) setReadFailure(null);
    void api
      .getSupportAccess()
      .then((next) => {
        if (!mounted.current || revision.current !== request) return;
        if (!isState(next)) throw new Error(readFailureText);
        setState(next);
        setReadFailure(null);
      })
      .catch((reason: unknown) => {
        // A background re-read that fails leaves the last known answer on screen.
        if (mounted.current && revision.current === request && !quiet)
          setReadFailure(sentence(reason, readFailureText));
      });
  }, []);

  useEffect(() => {
    mounted.current = true;
    read(false);
    const refresh = () => {
      if (!busy.current && document.visibilityState !== 'hidden') read(true);
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      mounted.current = false;
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [read]);

  // A Settings window left open across the end of the seven days shows off.
  const expiresAt = state?.on ? state.expiresAt : null;
  useEffect(() => {
    if (!expiresAt) return;
    const due = Date.parse(expiresAt);
    if (!Number.isFinite(due)) return;
    const wait = Math.min(
      Math.max(due + expiryReadDelayMilliseconds - Date.now(), 0),
      longestTimerMilliseconds,
    );
    const timer = window.setTimeout(() => {
      if (!busy.current) read(true);
    }, wait);
    return () => window.clearTimeout(timer);
  }, [expiresAt, read]);

  // While main answers, the switch is marked unavailable but never `disabled`: a disabled
  // control drops the keyboard's focus, and the person would have to find the switch again.
  const choose = (enabled: boolean) => {
    const api = desktop();
    if (busy.current || !state || !api?.setSupportAccess) return;
    busy.current = true;
    setChanging(true);
    setFailure(null);
    const request = ++revision.current;
    void api
      .setSupportAccess(enabled)
      .then((next) => {
        if (!mounted.current || revision.current !== request) return;
        if (!isState(next)) throw new Error(changeFailureText);
        setState(next);
      })
      .catch((reason: unknown) => {
        if (!mounted.current) return;
        // The switch goes back to whatever main now says, never to what was clicked.
        setFailure(sentence(reason, changeFailureText));
        busy.current = false;
        read(true);
      })
      .finally(() => {
        busy.current = false;
        if (mounted.current) setChanging(false);
      });
  };

  return (
    <section className="card support-access">
      {readFailure && !state ? (
        <div
          className="software-updates-setting-failure"
          role="alert"
          data-telemetry="error:support-access-load-failed"
        >
          <span>{readFailure}</span>
          <button type="button" className="quiet-button small" onClick={() => read(false)}>
            Try again
          </button>
        </div>
      ) : !state ? (
        <p className="software-updates-waiting">
          <LoaderCircle className="spin" size={16} />
          Loading…
        </p>
      ) : (
        <div className="early-access-row">
          <label className="early-access-toggle">
            <span>
              <strong>Support access</strong>
              <small id={descriptionId}>
                Lets Ademero support work on this computer. It turns itself off automatically, or
                you can turn it off.
              </small>
            </span>
            <input
              type="checkbox"
              role="switch"
              aria-label="Support access"
              aria-describedby={descriptionId}
              checked={state.on}
              aria-disabled={changing || undefined}
              aria-busy={changing || undefined}
              onChange={(event) => choose(event.target.checked)}
            />
            <span className="early-access-switch" aria-hidden="true" />
          </label>
        </div>
      )}
      {failure && (
        <p
          className="software-updates-failure"
          role="alert"
          data-telemetry="error:support-access-save-failed"
        >
          {failure}
        </p>
      )}
    </section>
  );
}
